import React from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE } from "@/components/animation/MotionShared";

/**
 * Animated Global Markets map — stylised pulse points over a low-poly
 * Mercator-like world silhouette. No proprietary geo data; just decorative
 * arcs + region pulses, with text-labels for accessibility.
 */

const REGIONS = [
  { id: "us",       label: "United States", x:  220, y: 175, status: "Active" },
  { id: "ca",       label: "Canada",        x:  220, y: 130, status: "Active" },
  { id: "latam",    label: "Latin America", x:  280, y: 305, status: "Active" },
  { id: "eu",       label: "Europe",        x:  460, y: 140, status: "Active" },
  { id: "africa-w", label: "Ghana · Nigeria", x: 470, y: 248, status: "Active" },
  { id: "africa-e", label: "Kenya · Rwanda",  x: 530, y: 270, status: "Active" },
  { id: "me",       label: "Middle East",   x:  555, y: 195, status: "Active" },
  { id: "asia",     label: "Asia",          x:  680, y: 185, status: "Active" },
  { id: "oceania",  label: "Oceania",       x:  720, y: 305, status: "Active" },
];

export default function GlobalMarketMap({ testid = "global-market-map", height = 360 } = {}) {
  const reduce = useReducedMotionSafe();
  const w = 880;
  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>Global Markets Live</span>
          <h3 style={S.title}>CareLogix ORACLE™ World Pulse</h3>
        </div>
        <span style={S.live}>● LIVE</span>
      </header>
      <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="xMidYMid meet" width="100%" height={height} role="img" aria-label="Global markets live map">
        <defs>
          <linearGradient id={`gmm-${testid}-water`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#031211" />
            <stop offset="100%" stopColor="#06231b" />
          </linearGradient>
          <radialGradient id={`gmm-${testid}-pulse`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={PALETTE.greenAccent} stopOpacity="0.6" />
            <stop offset="100%" stopColor={PALETTE.greenAccent} stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width={w} height={height} fill={`url(#gmm-${testid}-water)`} rx="14" />
        {/* Stylised continents — decorative low-poly silhouettes */}
        <g fill={PALETTE.bgGreen} fillOpacity="0.78" stroke={PALETTE.gold} strokeOpacity="0.16" strokeWidth="0.6">
          {/* N. America */}
          <polygon points="110,100 280,90 290,160 250,205 175,210 140,180 105,150" />
          {/* S. America */}
          <polygon points="230,225 310,225 325,300 285,335 245,330 230,290" />
          {/* Europe */}
          <polygon points="420,105 510,100 525,150 480,170 425,160" />
          {/* Africa */}
          <polygon points="440,170 525,170 545,260 505,310 470,310 435,255" />
          {/* Asia */}
          <polygon points="525,90 720,95 760,170 710,225 600,210 530,160" />
          {/* Oceania */}
          <polygon points="690,290 760,285 770,320 715,330" />
        </g>

        {/* Light grid lines */}
        {[1,2,3,4].map((i) => (
          <line key={i} x1="0" x2={w} y1={(height / 5) * i} y2={(height / 5) * i} stroke="rgba(255,255,255,0.04)" />
        ))}

        {/* Region pulses */}
        {REGIONS.map((r, i) => (
          <g key={r.id} data-testid={`${testid}-region-${r.id}`}>
            {!reduce && (
              <motion.circle cx={r.x} cy={r.y} r={6} fill={`url(#gmm-${testid}-pulse)`}
                initial={{ r: 6, opacity: 0.65 }}
                animate={{ r: [6, 22, 6], opacity: [0.65, 0.05, 0.65] }}
                transition={{ duration: 2.4, delay: i * 0.18, repeat: Infinity, ease: "easeOut" }}
              />
            )}
            <circle cx={r.x} cy={r.y} r={3.5} fill={PALETTE.greenAccent} stroke={PALETTE.goldBright} strokeWidth="1" />
            <text x={r.x + 8} y={r.y + 3.5} fill={PALETTE.goldBright} fontFamily="'Space Mono', monospace" fontSize="10" letterSpacing="0.06em">
              {r.label}
            </text>
          </g>
        ))}
      </svg>
      <ul style={S.legend} aria-label="Active markets">
        {REGIONS.map((r) => (
          <li key={r.id} style={S.legendItem}>
            <span style={S.dot} aria-hidden="true" />
            <strong style={S.legendLbl}>{r.label}</strong>
            <span style={S.legendStatus}>{r.status}</span>
          </li>
        ))}
        <li style={S.legendItem}>
          <span style={{ ...S.dot, background: PALETTE.gold }} aria-hidden="true" />
          <strong style={S.legendLbl}>Global</strong>
          <span style={S.legendStatus}>Live</span>
        </li>
      </ul>
      <span style={S.compliance}>Educational research only · not advice · jurisdictional rules apply.</span>
    </section>
  );
}

const S = {
  shell: { background: PALETTE.glassDark, border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 10, color: PALETTE.slate3, maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 },
  tagline: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: PALETTE.goldBright, margin: 0, fontSize: 16 },
  live: { color: "#ef4444", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, padding: "3px 8px", border: "1px solid #ef4444", borderRadius: 6, background: "rgba(239,68,68,0.10)" },
  legend: { listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6 },
  legendItem: { display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, fontSize: 11.5 },
  dot: { width: 9, height: 9, borderRadius: "50%", background: PALETTE.greenAccent, boxShadow: `0 0 10px ${PALETTE.greenAccent}` },
  legendLbl: { color: PALETTE.goldBright, fontFamily: "'Figtree', system-ui, sans-serif", flex: 1, fontSize: 11.5 },
  legendStatus: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.08em" },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5 },
};
